/**
 * Comprehensive filtering utility for the CRM system
 * Supports complex nested filter logic with PostgreSQL integration
 */

import type {
  FilterCondition,
  BasicFilterCondition,
  JoinFilterCondition,
  FilterOperator,
  PostgrestOperator,
  AGGridFilter,
  TransformedFilter,
  FilterValidationResult,
  FilterOperationContext
} from '../types/filters';
import type { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import type { QueryConditions } from '../types/supabase';
import { postgrestValue } from './postgrest';

/*
Sample complex usage:

const filters: FilterCondition[] = [
    {
        filterType: 'join',
        type: 'OR',
        conditions: [
            {
                filterType: 'join',
                type: 'AND',
                conditions: [
                    { filterType: 'text', colId: 'name', type: 'contains', filter: 'John' },
                    { filterType: 'number', colId: 'age', type: 'greaterThanOrEqual', filter: 18 }
                ]
            },
            {
                filterType: 'join',
                type: 'AND',
                conditions: [
                    { filterType: 'text', colId: 'name', type: 'contains', filter: 'Jane' },
                    { filterType: 'number', colId: 'age', type: 'greaterThanOrEqual', filter: 18 }
                ]
            }
        ]
    }
];

Which in SQL represents:
SELECT * FROM users WHERE (name ILIKE '%John%' AND age >= 18) OR (name ILIKE '%Jane%' AND age >= 18);
*/

/**
 * Converts QueryConditions format to FilterCondition[] format
 * @param queryConditions - QueryConditions object to convert
 * @returns Array of FilterCondition objects
 */
export const convertQueryConditionsToFilterConditions = (queryConditions: QueryConditions): FilterCondition[] => {
  const filterConditions: FilterCondition[] = [];
  
  for (const [fieldName, condition] of Object.entries(queryConditions)) {
    if (condition.filterType === 'join') {
      // Handle JoinFilterCondition - pass it through as-is
      filterConditions.push(condition);
    } else {
      // Handle BasicFilterCondition
      const basicCondition = condition as BasicFilterCondition;
      const filterCondition: BasicFilterCondition = {
        filterType: basicCondition.filterType,
        // The map key is a synthetic identifier for range-split entries
        // (updated_at__gte) and JSON paths (a.b); only the condition's own
        // colId is the real column. Falling back to the key kept plain
        // single-condition filters working while range filters sent the
        // synthetic key to PostgREST as a column name.
        colId: basicCondition.colId || fieldName,
        type: basicCondition.type,
        filter: basicCondition.filter,
        // Set filters carry their choices here; dropping them sent in.(undefined).
        ...(basicCondition.values ? { values: basicCondition.values } : {}),
        ...(basicCondition.filterTo !== undefined ? { filterTo: basicCondition.filterTo } : {}),
      };
      filterConditions.push(filterCondition);
    }
  }
  
  return filterConditions;
};

/**
 * Recursively flattens nested filter conditions to a single array
 * @param filters - Array of filter conditions to flatten
 * @returns Flattened array of basic filter conditions
 */
export const flattenFilter = (filters: FilterCondition[]): BasicFilterCondition[] => {
  const result: BasicFilterCondition[] = [];
  
  for (const filter of filters) {
    if (filter.filterType === 'join' && 'conditions' in filter) {
      result.push(...flattenFilter(filter.conditions));
    } else {
      result.push(filter as BasicFilterCondition);
    }
  }
  
  return result;
};

/**
 * Maps internal filter operators to PostgreSQL operators
 * @param operator - Internal filter operator
 * @returns Corresponding PostgreSQL operator
 * @throws Error if operator is not supported
 */
const getPostgrestOperator = (operator: FilterOperator): PostgrestOperator => {
  switch (operator) {
    case 'contains':
      return 'ilike';
    case 'equals':
      return 'eq';
    case 'notEqual':
      return 'neq';
    case 'startsWith':
      return 'ilike';
    case 'endsWith':
      return 'ilike';
    case 'lessThan':
      return 'lt';
    case 'lessThanOrEqual':
      return 'lte';
    case 'greaterThan':
      return 'gt';
    case 'greaterThanOrEqual':
      return 'gte';
    case 'blank':
      return 'is';
    case 'notBlank':
      return 'not';
    case 'in':
      return 'in';
    case 'notIn':
      return 'in'; // Note: notIn is handled differently in the query builder
    case 'set':
      return 'in';
    case 'plain_fts':
      return 'plfts';
    default:
      throw new Error(`Unsupported operator: ${operator}`);
  }
};

/**
 * Applies a basic filter condition to a query
 * @param query - Query builder instance
 * @param condition - Basic filter condition to apply
 * @returns Modified query with the filter applied
 */
const applyBasicFilter = <T extends PostgrestFilterBuilder<any, any, any, any>>(
  query: T, 
  condition: BasicFilterCondition
): T => {
  const { colId: column, type: operator, filter: value } = condition;
  
  switch (operator) {
    case 'contains':
      return query.ilike(column, `%${value}%`) as T;
    case 'equals':
      return query.eq(column, value) as T;
    case 'notEqual':
      return query.neq(column, value) as T;
    case 'startsWith':
      return query.ilike(column, `${value}%`) as T;
    case 'endsWith':
      return query.ilike(column, `%${value}`) as T;
    case 'lessThan':
      return query.lt(column, value) as T;
    case 'lessThanOrEqual':
      return query.lte(column, value) as T;
    case 'greaterThan':
      return query.gt(column, value) as T;
    case 'greaterThanOrEqual':
      return query.gte(column, value) as T;
    case 'blank':
      return query.is(column, null) as T;
    case 'notBlank':
      return query.not(column, 'is', null) as T;
    case 'in':
      return query.in(column, condition.values || [value]) as T;
    case 'notIn':
      return query.not(column, 'in', `(${(condition.values || [value]).map(postgrestValue).join(',')})`) as T;
    case 'set':
      return query.in(column, condition.values || [value]) as T;
    case 'plain_fts':
      return query.textSearch(column, value, {
        config: 'english',
        type: 'plain',
      }) as T;
    default:
      console.warn(`Unsupported operator: ${operator}`);
      return query;
  }
};

/**
 * Generates a raw PostgreSQL condition string for use in complex queries
 * @param condition - Basic filter condition
 * @returns Raw PostgreSQL condition string
 */
const getRawPostgrestCondition = (condition: BasicFilterCondition): string => {
  const { colId, type, filter } = condition;
  
  if (type === 'blank') {
    return `${colId}.is.null`;
  }
  if (type === 'notBlank') {
    return `${colId}.is.not.null`;
  }
  if (type === 'in' || type === 'notIn' || type === 'set') {
    const list = `(${(condition.values ?? [filter]).map(postgrestValue).join(',')})`;
    return type === 'notIn' ? `${colId}.not.in.${list}` : `${colId}.in.${list}`;
  }
  // Values sit inside a comma separated logic tree, so anything structural in
  // them must be quoted or the tree splits and PostgREST rejects the whole
  // query (REG-685: any business name containing a comma).
  if (type === 'contains') {
    return `${colId}.ilike.${postgrestValue(`*${filter}*`)}`;
  }
  if (type === 'startsWith') {
    return `${colId}.ilike.${postgrestValue(`${filter}*`)}`;
  }
  if (type === 'endsWith') {
    return `${colId}.ilike.${postgrestValue(`*${filter}`)}`;
  }

  return `${colId}.${getPostgrestOperator(type)}.${postgrestValue(filter)}`;
};

/**
 * Applies OR filter logic to a query
 * @param query - Query builder instance
 * @param conditions - Array of basic filter conditions to combine with OR
 * @returns Modified query with OR filter applied
 */
const applyOrFilter = <T extends PostgrestFilterBuilder<any, any, any, any>>(
  query: T, 
  conditions: BasicFilterCondition[]
): T => {
  const rawConditions = conditions.map(condition => getRawPostgrestCondition(condition));
  return query.or(rawConditions.join(',')) as T;
};

/**
 * Applies AND filter logic to a query
 * @param query - Query builder instance
 * @param conditions - Array of basic filter conditions to combine with AND
 * @returns Modified query with AND filter applied
 */
const applyAndFilter = <T extends PostgrestFilterBuilder<any, any, any, any>>(
  query: T, 
  conditions: BasicFilterCondition[]
): T => {
  return conditions.reduce((currentQuery, condition) => applyBasicFilter(currentQuery, condition), query);
};

/**
 * Transforms AG Grid filter format to internal filter format
 * @param filters - AG Grid filter object
 * @returns Array of transformed filter conditions
 */
export const transformAGGridFilter = (filters: AGGridFilter): TransformedFilter[] => {
  return Object.keys(filters).map(key => {
    const filter = filters[key];
    // Use colId if present, otherwise fall back to key
    const colId = filter.colId || key;
    
    return {
      colId,
      filterType: filter.filterType,
      type: filter.filterType === 'set' ? 'set' : (filter.type || 'equals'),
      filter: filter.filter || filter.values,
      values: filter.values
    };
  });
};

/**
 * Validates a filter condition for correctness and completeness
 * @param condition - Filter condition to validate
 * @returns Validation result with errors and warnings
 */
export const validateFilterCondition = (condition: FilterCondition): FilterValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!condition.filterType) {
    errors.push('Filter condition must have a filterType');
  }

  if (condition.filterType === 'join') {
    const joinCondition = condition as JoinFilterCondition;
    if (!joinCondition.type || !['AND', 'OR'].includes(joinCondition.type)) {
      errors.push('Join filter must have type "AND" or "OR"');
    }
    if (!joinCondition.conditions || joinCondition.conditions.length === 0) {
      errors.push('Join filter must have at least one condition');
    }
  } else {
    const basicCondition = condition as BasicFilterCondition;
    if (!basicCondition.colId) {
      errors.push('Basic filter condition must have a colId');
    }
    if (!basicCondition.type) {
      errors.push('Basic filter condition must have a type');
    }
    if (basicCondition.type !== 'blank' && basicCondition.type !== 'notBlank' && 
        basicCondition.filter === undefined && !basicCondition.values) {
      warnings.push('Filter condition has no filter value or values array');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Main function to apply filters to a query
 * Supports both AG Grid format and internal filter format
 * @param query - Query builder instance
 * @param filters - Filters to apply (AG Grid format, internal format, or array)
 * @param context - Optional context for logging and debugging
 * @returns Modified query with all filters applied
 */
export const applyFilters = <T extends PostgrestFilterBuilder<any, any, any, any>>(
  query: T,
  filters: AGGridFilter | FilterCondition[] | TransformedFilter[] | QueryConditions | null | undefined,
  _context?: FilterOperationContext
): T => {
  // Handle empty or null filters
  if (!filters || 
      (Array.isArray(filters) && filters.length === 0) || 
      (typeof filters === 'object' && Object.keys(filters).length === 0)) {
    return query;
  }

  let finalFilters: FilterCondition[] | TransformedFilter[];

  // Convert different input formats to internal format
  if (!Array.isArray(filters)) {
    // Check if it's QueryConditions format (has nested objects with filterType property)
    const firstValue = Object.values(filters)[0];
    if (firstValue && typeof firstValue === 'object' && 'filterType' in firstValue && 'type' in firstValue) {
      // This is QueryConditions format
      finalFilters = convertQueryConditionsToFilterConditions(filters as QueryConditions);
    } else {
      // This is AG Grid format
      const transformedFilter = transformAGGridFilter(filters as AGGridFilter);
      finalFilters = transformedFilter;
    }
  } else {
    finalFilters = filters;
  }

  // Apply each filter condition
  finalFilters.forEach(filter => {
    if ('filterType' in filter && filter.filterType === 'join') {
      const joinFilter = filter as JoinFilterCondition;
      if (joinFilter.type === 'OR') {
        query = applyOrFilter(query, joinFilter.conditions as BasicFilterCondition[]);
      } else if (joinFilter.type === 'AND') {
        query = applyAndFilter(query, joinFilter.conditions as BasicFilterCondition[]);
      }
    } else {
      query = applyBasicFilter(query, filter as BasicFilterCondition);
    }
  });

  return query;
};

/**
 * Type guard to check if a condition is a join condition
 * @param condition - Filter condition to check
 * @returns True if the condition is a join condition
 */
export const isJoinCondition = (condition: FilterCondition): condition is JoinFilterCondition => {
  return condition.filterType === 'join' && 'conditions' in condition;
};

/**
 * Type guard to check if a condition is a basic condition
 * @param condition - Filter condition to check
 * @returns True if the condition is a basic condition
 */
export const isBasicCondition = (condition: FilterCondition): condition is BasicFilterCondition => {
  return condition.filterType !== 'join' && 'colId' in condition;
};

/**
 * Counts the total number of conditions in a filter tree
 * @param filters - Array of filter conditions
 * @returns Total count of all conditions (including nested ones)
 */
export const countFilterConditions = (filters: FilterCondition[]): number => {
  let count = 0;
  
  for (const filter of filters) {
    if (isJoinCondition(filter)) {
      count += countFilterConditions(filter.conditions);
    } else {
      count += 1;
    }
  }
  
  return count;
};

/**
 * Gets the maximum nesting depth of filter conditions
 * @param filters - Array of filter conditions
 * @param currentDepth - Current depth level (used internally for recursion)
 * @returns Maximum nesting depth
 */
export const getFilterNestingDepth = (filters: FilterCondition[], currentDepth = 0): number => {
  let maxDepth = currentDepth;
  
  for (const filter of filters) {
    if (isJoinCondition(filter)) {
      const childDepth = getFilterNestingDepth(filter.conditions, currentDepth + 1);
      maxDepth = Math.max(maxDepth, childDepth);
    }
  }
  
  return maxDepth;
};

/**
 * Serializes filter conditions to a JSON string for storage or transmission
 * @param filters - Array of filter conditions
 * @returns JSON string representation of the filters
 */
export const serializeFilters = (filters: FilterCondition[]): string => {
  return JSON.stringify(filters, null, 2);
};

/**
 * Deserializes filter conditions from a JSON string
 * @param filtersJson - JSON string representation of filters
 * @returns Array of filter conditions
 * @throws Error if JSON is invalid or doesn't represent valid filters
 */
export const deserializeFilters = (filtersJson: string): FilterCondition[] => {
  try {
    const parsed = JSON.parse(filtersJson);
    if (!Array.isArray(parsed)) {
      throw new Error('Filters must be an array');
    }
    return parsed as FilterCondition[];
  } catch (error) {
    throw new Error(`Failed to deserialize filters: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Creates a human-readable description of filter conditions
 * @param filters - Array of filter conditions
 * @returns Human-readable string describing the filters
 */
export const describeFilters = (filters: FilterCondition[]): string => {
  const descriptions: string[] = [];
  
  for (const filter of filters) {
    if (isJoinCondition(filter)) {
      const childDescriptions = filter.conditions.map(child => describeFilters([child]));
      descriptions.push(`(${childDescriptions.join(` ${filter.type} `)})`);
    } else {
      const { colId, type, filter: value } = filter;
      descriptions.push(`${colId} ${type} ${value}`);
    }
  }
  
  return descriptions.join(' AND ');
};
